import { useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Divider } from '@/ui';
import * as s from './LoginPage.css';
import type { TurnstileInstance } from '@marsidev/react-turnstile';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  useCountdown,
  CODE_LENGTH,
  CODE_TTL_SECONDS,
  RESEND_DELAY_SECONDS,
  errorStatus,
  type CodeCellsHandle,
} from '@foundation-bb/foundation-auth-ui';
import { rysApi, useRequestEmailCodeMutation, useVerifyEmailCodeMutation } from '@/api/rysApi';
import { useAuthConfig } from '@/hooks/useAuthConfig';
import { setAuth, setMe } from '@/store/authSlice';
import type { AppDispatch } from '@/store/store';
import { SsoButtons } from '@/components/login/SsoButtons';
import { EmailStepForm } from '@/components/login/EmailStepForm';
import { CodeStepForm } from '@/components/login/CodeStepForm';
import { EMAIL_STORAGE_KEY } from '@/components/login/helpers';

type Step = 'email' | 'code';

export default function LoginPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const dispatchTyped = dispatch as AppDispatch;
  const authConfig = useAuthConfig();
  const [requestEmailCode] = useRequestEmailCodeMutation();
  const [verifyEmailCode] = useVerifyEmailCodeMutation();
  const returnUrl = `${window.location.origin}/sso/callback`;

  const turnstileSiteKey = authConfig?.turnstile.siteKey ?? null;
  const turnstileBypassed = authConfig?.turnstile.bypassed ?? false;
  const turnstileEnabled = !!turnstileSiteKey && !turnstileBypassed;

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState(() => localStorage.getItem(EMAIL_STORAGE_KEY) ?? '');
  const [code, setCode] = useState<string[]>(Array(CODE_LENGTH).fill(''));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [codeIssuedAt, setCodeIssuedAt] = useState<number | null>(null);

  const turnstileRef = useRef<TurnstileInstance | null>(null);
  const codeCellsRef = useRef<CodeCellsHandle | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string>('');
  const turnstileReady = !turnstileEnabled || turnstileToken.length > 0;

  const { remainingSeconds: countdown, elapsedSeconds } = useCountdown(
    codeIssuedAt,
    CODE_TTL_SECONDS,
    step === 'code',
  );
  const resendAvailable = elapsedSeconds >= RESEND_DELAY_SECONDS;

  function handleRequestError(err: unknown) {
    if (errorStatus(err) === 429) setError(t('login.errorRateLimited'));
    else setError(t('login.errorRequestFailed'));
  }

  async function sendCode(): Promise<boolean> {
    try {
      await requestEmailCode({ email, turnstileToken: turnstileToken || null }).unwrap();
      return true;
    } catch (err) {
      handleRequestError(err);
      return false;
    } finally {
      setTurnstileToken('');
      turnstileRef.current?.reset();
    }
  }

  async function onSendCodeSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const ok = await sendCode();
    setSubmitting(false);
    if (ok) {
      localStorage.setItem(EMAIL_STORAGE_KEY, email);
      setCode(Array(CODE_LENGTH).fill(''));
      setCodeIssuedAt(Date.now());
      setStep('code');
    }
  }

  async function onResend() {
    setError(null);
    setCodeIssuedAt(Date.now());
    await sendCode();
  }

  async function submitCode(values: string[]) {
    const fullCode = values.join('');
    if (fullCode.length !== CODE_LENGTH) return;

    setError(null);
    setSubmitting(true);
    try {
      const auth = await verifyEmailCode({ email, code: fullCode }).unwrap();
      dispatch(setAuth(auth));
      const me = await dispatchTyped(rysApi.endpoints.getMe.initiate(undefined, { forceRefetch: true })).unwrap();
      dispatch(setMe(me));
      navigate('/dashboard', { replace: true });
    } catch (err) {
      const status = errorStatus(err);
      if (status === 401 || status === 400) setError(t('login.errorInvalidCode'));
      else setError(t('login.errorRequestFailed'));
      setCode(Array(CODE_LENGTH).fill(''));
      codeCellsRef.current?.focusFirst();
    } finally {
      setSubmitting(false);
    }
  }

  function useDifferentEmail() {
    setStep('email');
    setCode(Array(CODE_LENGTH).fill(''));
    setError(null);
  }

  return (
    <div className={s.page}>
      <Card id="login-card" className={s.loginCard}>
        <h1 className={s.title}>{t('login.title')}</h1>
        <p className={s.subtitle}>{t('login.subtitle')}</p>

        <SsoButtons returnUrl={returnUrl} />

        <Divider text={t('login.or')} />

        {step === 'email' ? (
          <EmailStepForm
            ref={turnstileRef}
            email={email}
            onEmailChange={setEmail}
            onSubmit={onSendCodeSubmit}
            submitting={submitting}
            error={error}
            turnstileEnabled={turnstileEnabled}
            turnstileSiteKey={turnstileSiteKey}
            turnstileReady={turnstileReady}
            onTurnstileToken={setTurnstileToken}
          />
        ) : (
          <CodeStepForm
            ref={codeCellsRef}
            email={email}
            code={code}
            onCodeChange={setCode}
            onCodeComplete={values => void submitCode(values)}
            onSubmit={e => { e.preventDefault(); void submitCode(code); }}
            submitting={submitting}
            error={error}
            countdown={countdown}
            resendAvailable={resendAvailable}
            onResend={onResend}
            onUseDifferentEmail={useDifferentEmail}
          />
        )}
      </Card>
    </div>
  );
}
